import { memo, useMemo } from 'react'
import { IconCloseButton } from '@/components/Button/IconCloseButton'
import { ActiveClientsMetricsWidget } from '@/components/Metrics/ActiveClientsMetricsWidget'
import { ActiveTAMetricsWidget } from '@/components/Metrics/ActiveTAMetricsWidget'
import { CJMWidget } from '@/components/Metrics/CJMWidget'
import { ComparisonMetricsWidget } from '@/components/Metrics/ComparisonMetricsWidget'
import { isChartWidgetType } from '@/components/Metrics/helpers/isChartWidgetType'
import { MetricsWidget } from '@/components/Metrics/MetricsWidget'

import { Widget, WidgetType } from '../types'

interface WidgetContainerProps {
  widget: Widget
  widgetId: string
  isEditMode: boolean
  onRemove: (widgetId: string) => void
  filters: {
    introducingBrokerIds?: string
    countryIds?: string
    customDateRange?: [string, string]
    intervalType?: number
  } | null
}

type WidgetProps = {
  introducingBrokerIds?: string[]
  countryIds?: string[]
  customDateRange?: [string, string]
  intervalType?: number
}

const renderWidget = (widget: Widget, widgetProps: WidgetProps) => {
  if (isChartWidgetType(widget.type)) {
    return <MetricsWidget type={widget.type} {...widgetProps} />
  }

  switch (widget.type) {
    case WidgetType.IB_METRICS_COMPARISON:
      return <ComparisonMetricsWidget {...widgetProps} />
    case WidgetType.ACTIVE_CLIENTS:
      return <ActiveClientsMetricsWidget {...widgetProps} />
    case WidgetType.ACTIVE_TA:
      return <ActiveTAMetricsWidget {...widgetProps} />
    case WidgetType.CJM:
      return <CJMWidget />
    default:
      return <div>Unknown widget type</div>
  }
}

export const WidgetContainer: React.FC<WidgetContainerProps> = memo(
  ({ widget, widgetId, isEditMode, onRemove, filters }) => {
    const widgetProps = useMemo(
      () => ({
        introducingBrokerIds: filters?.introducingBrokerIds?.split(','),
        countryIds: filters?.countryIds?.split(','),
        customDateRange: filters?.customDateRange,
        intervalType: filters?.intervalType,
      }),
      [filters]
    )

    return (
      <div className='group relative flex h-full max-h-full flex-col overflow-hidden rounded-lg'>
        <div className='shrink-0'>
          {isEditMode && (
            <IconCloseButton
              iconSize={12}
              onClick={() => onRemove(widgetId)}
              className='
            no-drag
            absolute right-2 top-0 z-10
            flex items-center justify-center
            opacity-0 transition-opacity duration-200
            cursor-pointer
            group-hover:opacity-100
            active:scale-90
          '
            />
          )}
        </div>

        <div className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-lg'>
          {renderWidget(widget, widgetProps)}
        </div>
      </div>
    )
  }
)
